/**
 * MSG-Q01 — Shared guided Request Quote field contract + message formatter.
 * Answers become one logical CommunicationService message (8–2000 chars).
 * Do not put form answers in URLs, analytics, public projections, or logs.
 */

export const guidedRequestQuoteMessageMinLength = 8;
export const guidedRequestQuoteMessageMaxLength = 2000;
export const guidedRequestQuoteSchemaVersion = "guided_request_quote.v1" as const;

export const guidedRequestQuoteServiceOptions = [
  "inspection",
  "mapping_survey",
  "photo_video",
  "thermal",
  "delivery_logistics",
  "other",
] as const;
export type GuidedRequestQuoteServiceOption =
  (typeof guidedRequestQuoteServiceOptions)[number];

export const guidedRequestQuoteTimingOptions = [
  "asap",
  "this_week",
  "this_month",
  "flexible",
  "specific_date",
] as const;
export type GuidedRequestQuoteTimingOption =
  (typeof guidedRequestQuoteTimingOptions)[number];

export const guidedRequestQuoteBudgetOptions = [
  "under_1k",
  "1k_5k",
  "5k_15k",
  "15k_plus",
  "discuss",
] as const;
export type GuidedRequestQuoteBudgetOption =
  (typeof guidedRequestQuoteBudgetOptions)[number];

export type GuidedRequestQuoteAnswers = Readonly<{
  serviceNeeded: GuidedRequestQuoteServiceOption | "";
  projectLocation: string;
  preferredTiming: GuidedRequestQuoteTimingOption | "";
  timingNotes: string;
  scopeSummary: string;
  budgetRange: GuidedRequestQuoteBudgetOption | "";
  additionalNotes: string;
}>;

export type GuidedRequestQuoteFieldKey = keyof GuidedRequestQuoteAnswers;

export type GuidedRequestQuoteFieldErrors = Partial<
  Readonly<Record<GuidedRequestQuoteFieldKey | "message", string>>
>;

export type GuidedRequestQuoteLabels = Readonly<{
  heading: string;
  reviewHeading: string;
  serviceNeededLabel: string;
  serviceNeededPlaceholder: string;
  serviceOptions: Readonly<Record<GuidedRequestQuoteServiceOption, string>>;
  projectLocationLabel: string;
  projectLocationPlaceholder: string;
  preferredTimingLabel: string;
  preferredTimingPlaceholder: string;
  timingOptions: Readonly<Record<GuidedRequestQuoteTimingOption, string>>;
  timingNotesLabel: string;
  timingNotesPlaceholder: string;
  scopeSummaryLabel: string;
  scopeSummaryPlaceholder: string;
  budgetRangeLabel: string;
  budgetRangePlaceholder: string;
  budgetOptions: Readonly<Record<GuidedRequestQuoteBudgetOption, string>>;
  additionalNotesLabel: string;
  additionalNotesPlaceholder: string;
  requiredField: string;
  messageTooShort: string;
  messageTooLong: string;
  messagePreviewLabel: string;
  backToForm: string;
  reviewMessage: string;
  submit: string;
  submitting: string;
  cancel: string;
  close: string;
  successTitle: string;
  successBody: string;
  openConversation: string;
  submitFailed: string;
  rateLimited: string;
  duplicate: string;
  messageLineService: string;
  messageLineLocation: string;
  messageLineTiming: string;
  messageLineScope: string;
  messageLineBudget: string;
  messageLineNotes: string;
  messageTitle: string;
}>;

export const emptyGuidedRequestQuoteAnswers = (): GuidedRequestQuoteAnswers => ({
  serviceNeeded: "",
  projectLocation: "",
  preferredTiming: "",
  timingNotes: "",
  scopeSummary: "",
  budgetRange: "",
  additionalNotes: "",
});

function trimField(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function trimMultiline(value: string): string {
  return value
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .trim();
}

export function sanitizeGuidedRequestQuoteAnswers(
  answers: GuidedRequestQuoteAnswers,
): GuidedRequestQuoteAnswers {
  const serviceNeeded = guidedRequestQuoteServiceOptions.includes(
    answers.serviceNeeded as GuidedRequestQuoteServiceOption,
  )
    ? (answers.serviceNeeded as GuidedRequestQuoteServiceOption)
    : "";
  const preferredTiming = guidedRequestQuoteTimingOptions.includes(
    answers.preferredTiming as GuidedRequestQuoteTimingOption,
  )
    ? (answers.preferredTiming as GuidedRequestQuoteTimingOption)
    : "";
  const budgetRange = guidedRequestQuoteBudgetOptions.includes(
    answers.budgetRange as GuidedRequestQuoteBudgetOption,
  )
    ? (answers.budgetRange as GuidedRequestQuoteBudgetOption)
    : "";

  return {
    serviceNeeded,
    projectLocation: trimField(answers.projectLocation).slice(0, 200),
    preferredTiming,
    timingNotes: trimField(answers.timingNotes).slice(0, 200),
    scopeSummary: trimMultiline(answers.scopeSummary).slice(0, 1200),
    budgetRange,
    additionalNotes: trimMultiline(answers.additionalNotes).slice(0, 600),
  };
}

export function formatGuidedRequestQuoteMessage(
  answers: GuidedRequestQuoteAnswers,
  labels: GuidedRequestQuoteLabels,
): string {
  const clean = sanitizeGuidedRequestQuoteAnswers(answers);
  const lines: string[] = [labels.messageTitle, ""];

  if (clean.serviceNeeded) {
    lines.push(
      `${labels.messageLineService}: ${labels.serviceOptions[clean.serviceNeeded]}`,
    );
  }
  if (clean.projectLocation) {
    lines.push(`${labels.messageLineLocation}: ${clean.projectLocation}`);
  }
  if (clean.preferredTiming) {
    const timingLabel = labels.timingOptions[clean.preferredTiming];
    const timing = clean.timingNotes
      ? `${timingLabel} (${clean.timingNotes})`
      : timingLabel;
    lines.push(`${labels.messageLineTiming}: ${timing}`);
  }
  if (clean.scopeSummary) {
    lines.push(`${labels.messageLineScope}:`);
    lines.push(clean.scopeSummary);
  }
  if (clean.budgetRange) {
    lines.push(
      `${labels.messageLineBudget}: ${labels.budgetOptions[clean.budgetRange]}`,
    );
  }
  if (clean.additionalNotes) {
    lines.push(`${labels.messageLineNotes}:`);
    lines.push(clean.additionalNotes);
  }

  // Never silently truncate participant-authored content. Validation must make
  // an oversized request actionable before it reaches the backend boundary.
  return lines.join("\n").trim();
}

export function validateGuidedRequestQuoteAnswers(
  answers: GuidedRequestQuoteAnswers,
  labels: GuidedRequestQuoteLabels,
): GuidedRequestQuoteFieldErrors {
  const clean = sanitizeGuidedRequestQuoteAnswers(answers);
  const errors: Record<string, string> = {};

  if (!clean.serviceNeeded) {
    errors.serviceNeeded = labels.requiredField;
  }
  if (!clean.projectLocation) {
    errors.projectLocation = labels.requiredField;
  }
  if (!clean.preferredTiming) {
    errors.preferredTiming = labels.requiredField;
  }
  if (!clean.scopeSummary || clean.scopeSummary.length < 8) {
    errors.scopeSummary = labels.requiredField;
  }

  const message = formatGuidedRequestQuoteMessage(clean, labels);
  if (message.length < guidedRequestQuoteMessageMinLength) {
    errors.message = labels.messageTooShort;
  } else if (message.length > guidedRequestQuoteMessageMaxLength) {
    errors.message = labels.messageTooLong;
  }

  return errors;
}

export function isGuidedRequestQuoteValid(
  answers: GuidedRequestQuoteAnswers,
  labels: GuidedRequestQuoteLabels,
): boolean {
  return Object.keys(validateGuidedRequestQuoteAnswers(answers, labels)).length === 0;
}
